'use client'

import dynamic from 'next/dynamic'
import Link from 'next/link'
import Image from 'next/image'
import { motion } from 'framer-motion'
import 'swiper/css'
import 'swiper/css/pagination'
import { useHydrated } from '@/hooks/useHydrated'
import { useIsDesktop } from '@/hooks/useIsDesktop'
import FeatureSwiper from '@/components/Swiper'

// Dynamic import for FeatureBox component
const FeatureBox = dynamic(() => import('./components/FeatureBox'))

// Dynamic import for Swiper components, ssr: false
const Swiper = dynamic(() => import('swiper/react').then((mod) => mod.Swiper), { ssr: false })
const SwiperSlide = dynamic(() => import('swiper/react').then((mod) => mod.SwiperSlide), {
  ssr: false,
})

export default function MainGermany() {
  const hydrated = useHydrated()
  const isDesktop = useIsDesktop()

  return (
    <div className="sm:min-h-screen">
      {/* Hero Background */}
      <div className="absolute inset-0 -z-10">
        {/* Mobile Image */}
        <div className="relative block h-full sm:hidden">
          <Image
            src="/images/home-mobile.webp"
            alt="Hero Background"
            fill
            priority
            className="object-cover"
            style={{ objectPosition: 'center 20%' }}
            sizes="(max-width: 640px) 100vw, 0vw"
          />
          <div className="absolute inset-0 bg-black opacity-30"></div>
        </div>

        {/* Desktop Video - Only render after hydration and on desktop */}
        {hydrated && isDesktop && (
          <video
            autoPlay
            playsInline
            muted
            loop={false}
            preload="metadata"
            poster="/images/main-hero-fallback.webp"
            className="hidden h-full w-full object-cover sm:block"
            style={{ objectPosition: 'center 20%' }}
            onLoadedMetadata={(e) => {
              e.currentTarget.currentTime = 0
            }}
            onEnded={(e) => {
              e.currentTarget.pause()
            }}
          >
            <source src="/videos/main-germany-video.webm" type="video/webm" />
            Your browser does not support video playback.
          </video>
        )}

        {/* Desktop Fallback Image - Show while video loads or if not desktop */}
        {hydrated && !isDesktop && (
          <div className="relative hidden h-full sm:block">
            <Image
              src="/images/main-hero-fallback.webp"
              alt="Hero Background"
              fill
              priority
              className="object-cover"
              style={{ objectPosition: 'center 20%' }}
              sizes="(min-width: 640px) 100vw, 0vw"
            />
          </div>
        )}

        {/* Server-side fallback image for desktop */}
        {!hydrated && (
          <div className="relative hidden h-full sm:block">
            <Image
              src="/images/main-hero-fallback.webp"
              alt="Hero Background"
              fill
              priority
              className="object-cover"
              style={{ objectPosition: 'center 20%' }}
              sizes="(min-width: 640px) 100vw, 0vw"
            />
          </div>
        )}

        <div className="absolute inset-0 bg-black opacity-15"></div>
      </div>

      {/* Main Content Container */}
      <div className="relative z-10 mx-auto max-w-7xl">
        <div className="flex-col pt-28 sm:block sm:min-h-screen sm:px-6 md:px-8 lg:px-10">
          {/* Hero Content */}
          <div className="flex flex-grow flex-col justify-center gap-8 text-center sm:absolute sm:inset-0 sm:mb-60 md:text-left">
            <div className="flex items-center justify-center text-base font-medium text-white md:justify-start md:text-lg">
              <svg
                className="mr-2 h-5 w-5 md:h-6 md:w-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                ></path>
              </svg>
              <span>Die Volks-Energielösung</span>
            </div>

            <h1 className="text-2xl leading-tight font-bold text-white sm:text-3xl md:text-4xl lg:text-5xl">
              <span className="sm:hidden">
                Unabhängigkeit mit Photovoltaik, Isolierung und Heizsystemen die sich jeder leisten
                kann!
              </span>
              <span className="hidden sm:inline">
                Unabhängigkeit mit
                <br />
                Photovoltaik, Isolierung
                <br />
                und Heizsystemen die sich
                <br />
                jeder leisten kann!
              </span>
            </h1>

            <div className="mb-2 md:mb-18">
              <Link
                href="/termin"
                className="inline-block transform rounded-lg bg-[#ff4500] px-6 py-3 font-bold text-white shadow-lg transition-all hover:scale-105 hover:bg-[#e03e00] md:px-8 md:py-3"
              >
                Volks-Lösung holen
              </Link>
            </div>
          </div>

          {/* Phone number - Mobile only */}
          {hydrated && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="mt-8 mb-8 flex justify-center md:hidden"
            >
              <Link
                href="tel:+49302239953879"
                className="inline-flex items-center space-x-2 text-white transition-colors hover:text-[#ff4500]"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={3}
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                  />
                </svg>
                <span className="text-lg font-bold">030 223 995 3879</span>
              </Link>
            </motion.div>
          )}

          {/* Features section */}
          <div className="mt-4 sm:absolute sm:right-0 sm:bottom-0 sm:left-0 sm:z-10 sm:pb-8 md:pb-10">
            {/* Visually hidden heading for semantic structure */}
            <h2 className="sr-only">Unsere Vorteile</h2>
            <div className="rounded-lg bg-black/40 pt-4 sm:bg-gradient-to-b sm:from-transparent sm:to-black/80 lg:py-8">
              <div className="mx-auto max-w-7xl px-4 sm:px-6 md:px-8 lg:px-10">
                {/* Mobile Slider */}
                <FeatureSwiper />

                {/* Desktop Grid */}
                <div className="hidden lg:grid lg:grid-cols-3 lg:gap-8">
                  <FeatureBox
                    number="01"
                    title="0% Anzahlung"
                    description="Sie zahlen erst bei Leistung. So haben Sie kaum Risiko."
                  />
                  <FeatureBox
                    number="02"
                    title="Günstiger Preis"
                    description="Welche Lösung auch immer die Beste für Sie ist: Unsere Angebote zählen zu den Günstigsten, die Sie kriegen können."
                  />
                  <FeatureBox
                    number="03"
                    title="Qualität"
                    description="Trotz günstiger Preise bieten wir hochwertige Markenprodukte und setzen auf deutsches Handwerk."
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
